var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var _app, _connector;
import { Contract, utils } from 'ethers';
import { subscription, arbitratorAbi } from '@1hive/connect-core';
import { bn } from '../helpers';
export default class Agreement {
    constructor(connector, app) {
        _app.set(this, void 0);
        _connector.set(this, void 0);
        __classPrivateFieldSet(this, _connector, connector);
        __classPrivateFieldSet(this, _app, app);
        this.address = app.address;
    }
    async disconnect() {
        await __classPrivateFieldGet(this, _connector).disconnect();
    }
    async id() {
        const data = await __classPrivateFieldGet(this, _connector).agreement(this.address);
        return data.id;
    }
    async dao() {
        const data = await __classPrivateFieldGet(this, _connector).agreement(this.address);
        return data.dao;
    }
    async stakingFactory() {
        const data = await __classPrivateFieldGet(this, _connector).agreement(this.address);
        return data.stakingFactory;
    }
    async currentVersion() {
        return __classPrivateFieldGet(this, _connector).currentVersion(this.address);
    }
    onCurrentVersion(callback) {
        return subscription(callback, (callback) => __classPrivateFieldGet(this, _connector).onCurrentVersion(this.address, callback));
    }
    versionId(versionNumber) {
        return `${this.address}-version-${versionNumber}`;
    }
    async version(versionNumber) {
        return __classPrivateFieldGet(this, _connector).version(this.versionId(versionNumber));
    }
    onVersion(versionNumber, callback) {
        return subscription(callback, (callback) => __classPrivateFieldGet(this, _connector).onVersion(this.versionId(versionNumber), callback));
    }
    async disputeFees(versionId) {
        const version = await __classPrivateFieldGet(this, _connector).version(versionId);
        const arbitrator = new Contract(version.arbitrator, arbitratorAbi, __classPrivateFieldGet(this, _app).provider);
        return arbitrator.getDisputeFees();
    }
    async versions({ first = 1000, skip = 0 } = {}) {
        return __classPrivateFieldGet(this, _connector).versions(this.address, first, skip);
    }
    onVersions({ first = 1000, skip = 0 } = {}, callback) {
        return subscription(callback, (callback) => __classPrivateFieldGet(this, _connector).onVersions(this.address, first, skip, callback));
    }
    async disputableApps({ first = 1000, skip = 0 } = {}) {
        return __classPrivateFieldGet(this, _connector).disputableApps(this.address, first, skip);
    }
    onDisputableApps({ first = 1000, skip = 0 } = {}, callback) {
        return subscription(callback, (callback) => __classPrivateFieldGet(this, _connector).onDisputableApps(this.address, first, skip, callback));
    }
    signerId(signerAddress) {
        return `${this.address}-signer-${signerAddress.toLowerCase()}`;
    }
    async signer(signerAddress) {
        return __classPrivateFieldGet(this, _connector).signer(this.signerId(signerAddress));
    }
    onSigner(signerAddress, callback) {
        return subscription(callback, (callback) => __classPrivateFieldGet(this, _connector).onSigner(this.signerId(signerAddress), callback));
    }
    stakingId(tokenAddress, userAddress) {
        return `${tokenAddress.toLowerCase()}-staking-${userAddress.toLowerCase()}`;
    }
    async staking(tokenAddress, userAddress) {
        return __classPrivateFieldGet(this, _connector).staking(this.stakingId(tokenAddress, userAddress));
    }
    onStaking(tokenAddress, userAddress, callback) {
        return subscription(callback, (callback) => __classPrivateFieldGet(this, _connector).onStaking(this.stakingId(tokenAddress, userAddress), callback));
    }
    async stakingMovements(tokenAddress, userAddress, { first = 1000, skip = 0 } = {}) {
        return __classPrivateFieldGet(this, _connector).stakingMovements(this.stakingId(tokenAddress, userAddress), this.address, first, skip);
    }
    onStakingMovements(tokenAddress, userAddress, { first = 1000, skip = 0 } = {}, callback) {
        return subscription(callback, (callback) => __classPrivateFieldGet(this, _connector).onStakingMovements(this.stakingId(tokenAddress, userAddress), this.address, first, skip, callback));
    }
    actionId(actionNumber) {
        return `${this.address.toLowerCase()}-action-${actionNumber}`;
    }
    async action(actionNumber) {
        const action = await this.tryAction(actionNumber);
        if (!action) {
            throw Error(`Could not find given action number ${actionNumber}`);
        }
        return action;
    }
    async tryAction(actionNumber) {
        return __classPrivateFieldGet(this, _connector).action(this.actionId(actionNumber));
    }
    onAction(actionNumber, callback) {
        return subscription(callback, (callback) => __classPrivateFieldGet(this, _connector).onAction(this.actionId(actionNumber), callback));
    }
    async sign(signerAddress, versionNumber) {
        if (!versionNumber) {
            versionNumber = (await this.currentVersion()).versionId;
        }
        return __classPrivateFieldGet(this, _app).intent('sign', [versionNumber], { actAs: signerAddress });
    }
    async challenge(actionNumber, settlementOffer, finishedEvidence, context, signerAddress) {
        const intent = await __classPrivateFieldGet(this, _app).intent('challengeAction', [actionNumber, settlementOffer, finishedEvidence, utils.toUtf8Bytes(context)], { actAs: signerAddress });
        const action = await this.action(actionNumber);
        const { feeToken, feeAmount } = await this.disputeFees(action.versionId);
        const { tokenId: collateralToken, challengeAmount } = await action.collateralRequirement();
        const challengeCollateral = bn(challengeAmount);
        // approve challenge collateral and dispute fees
        const preTransactions = [];
        if (feeToken.toLowerCase() == collateralToken.toLowerCase()) {
            const approvalAmount = challengeCollateral.add(feeAmount);
            const approvePreTransactions = await intent.buildApprovePreTransactions({ address: feeToken, value: approvalAmount });
            preTransactions.push(...approvePreTransactions);
        }
        else {
            const feesPreTransactions = await intent.buildApprovePreTransactions({ address: feeToken, value: feeAmount });
            const collateralPreTransactions = await intent.buildApprovePreTransactions({ address: collateralToken, value: challengeCollateral });
            preTransactions.push(...feesPreTransactions, ...collateralPreTransactions);
        }
        intent.applyPreTransactions(preTransactions);
        return intent;
    }
    async dispute(actionNumber, finishedEvidence, signerAddress) {
        const intent = await __classPrivateFieldGet(this, _app).intent('disputeAction', [actionNumber, finishedEvidence], { actAs: signerAddress });
        const action = await this.action(actionNumber);
        const { feeToken, feeAmount } = await this.disputeFees(action.versionId);
        // approve dispute fees
        const preTransactions = await intent.buildApprovePreTransactions({ address: feeToken, value: feeAmount });
        intent.applyPreTransactions(preTransactions);
        return intent;
    }
    settle(actionNumber, signerAddress) {
        return __classPrivateFieldGet(this, _app).intent('settleAction', [actionNumber], { actAs: signerAddress });
    }
    close(actionNumber, signerAddress) {
        return __classPrivateFieldGet(this, _app).intent('closeAction', [actionNumber], { actAs: signerAddress });
    }
}
_app = new WeakMap(), _connector = new WeakMap();
